import React,{useState,useEffect} from 'react';
import {supabase} from '../../supabaseClient';
import SafeIcon from '../../common/SafeIcon';

const SettingsSection=({trainerId})=> {
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [profile,setProfile]=useState(null);
  const [tiers,setTiers]=useState([]);
  const [success,setSuccess]=useState(false);
  const [error,setError]=useState('');

  useEffect(()=> {
    if (trainerId) fetchSettings();
  },[trainerId]);

  const fetchSettings=async ()=> {
    setLoading(true);
    setError('');
    try {
      const {data,error: fetchError}=await supabase
        .from('trainer_profiles')
        .select('*')
        .eq('id',trainerId)
        .single();
      
      if (fetchError) throw fetchError;
      if (data) {
        setProfile(data);
        const sanitizedTiers=(data.pricing_tiers || []).map(tier=> ({
          id: tier.id || Math.random().toString(36).substr(2,9),
          name: tier.name || '',
          price: String(tier.price || '0'),
          description: tier.description || '',
          payment_link: tier.payment_link || '',
          learn_more_url: tier.learn_more_url || '',
          features: Array.isArray(tier.features) ? [...tier.features] : [],
          popular: !!tier.popular
        }));
        setTiers(sanitizedTiers);
      }
    } catch (err) {
      console.error('Fetch settings error:',err);
      setError('Failed to load settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleTierChange=(index,field,value)=> {
    const newTiers=[...tiers];
    if (field==='features') {
      newTiers[index][field]=value.split('\n').filter(f=> f && f.trim() !=='');
    } else {
      newTiers[index][field]=value;
    }
    setTiers(newTiers);
  };

  const handleAddTier=()=> {
    setTiers([
      ...tiers,
      {
        id: Math.random().toString(36).substr(2,9),
        name: 'New Training Plan',
        price: '0',
        description: '',
        payment_link: '',
        learn_more_url: '',
        features: ['Feature 1','Feature 2'],
        popular: false
      }
    ]);
  };

  const handleRemoveTier=(index)=> {
    if (window.confirm('Are you sure you want to remove this pricing tier?')) {
      setTiers(tiers.filter((_,i)=> i !==index));
    }
  };

  const saveSettings=async ()=> {
    if (!trainerId || !profile) return;
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      const cleanedTiers=tiers.map(t=> ({
        id: t.id,
        name: (t.name || 'Untitled Tier').trim(),
        price: String(t.price || '0').trim(),
        description: (t.description || '').trim(),
        payment_link: (t.payment_link || '').trim(),
        learn_more_url: (t.learn_more_url || '').trim(),
        features: (t.features || []).map(f=> f.trim()).filter(Boolean),
        popular: !!t.popular
      }));

      const {data,error: updateError}=await supabase
        .from('trainer_profiles')
        .update({
          business_name: (profile.business_name || '').trim(),
          terms_url: (profile.terms_url || '').trim(),
          signup_url: (profile.signup_url || '').trim(),
          pricing_tiers: cleanedTiers,
          updated_at: new Date().toISOString()
        })
        .eq('id',trainerId)
        .select();

      if (updateError) throw updateError;
      if (data && data.length > 0) {
        setProfile(data[0]);
        setTiers(data[0].pricing_tiers);
        setSuccess(true);
        setTimeout(()=> setSuccess(false),3000);
      }
    } catch (err) {
      console.error('Save error:',err);
      setError(err.message || 'Failed to update settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 animate-pulse space-y-4">{[1,2,3].map(i=> <div key={i} className="h-24 bg-white rounded-3xl" />)}</div>;

  return (
    <div className="space-y-8 max-w-5xl mx-auto pb-20 px-4">
      {/* Header Bar */}
      <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col md:flex-row justify-between items-center sticky top-20 z-10 gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-900">Portal Settings</h2>
          <p className="text-sm text-gray-500 font-medium">Manage the links and info shown to dog owners</p>
        </div>
        <button 
          onClick={saveSettings} 
          disabled={saving}
          className="w-full md:w-auto bg-indigo-600 text-white px-10 py-4 rounded-2xl font-black shadow-xl shadow-indigo-100 flex items-center justify-center gap-2 hover:bg-indigo-700 transition-all active:scale-95 disabled:opacity-50"
        >
          {saving ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <><SafeIcon name="Check" /> Save & Update Live</>
          )}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-5 rounded-2xl font-bold border border-red-100 flex items-center gap-3">
          <SafeIcon name="AlertTriangle" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-600 text-white p-5 rounded-2xl font-bold shadow-lg flex items-center gap-3">
          <SafeIcon name="CheckCircle" />
          <p>Settings updated successfully!</p>
        </div>
      )}

      {/* Business Info Section */}
      <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 block">Display Business Name</label>
            <input 
              value={profile?.business_name || ''} 
              onChange={e=> setProfile({...profile,business_name: e.target.value})}
              className="w-full px-5 py-3 bg-gray-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-indigo-500 transition-all text-gray-900" 
              placeholder="e.g. Elite K9 Training"
            />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 block">Terms & Conditions URL</label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                <SafeIcon name="Link" className="w-4 h-4" />
              </div>
              <input 
                value={profile?.terms_url || ''} 
                onChange={e=> setProfile({...profile,terms_url: e.target.value})}
                className="w-full pl-12 pr-5 py-3 bg-gray-50 rounded-2xl font-bold text-sm outline-none border-2 border-transparent focus:border-indigo-500 transition-all text-gray-700" 
                placeholder="https://yourwebsite.com/terms"
              />
            </div>
          </div>
        </div>

        <div className="space-y-2 pt-4 border-t border-gray-50">
          <label className="text-[10px] font-black text-indigo-500 uppercase tracking-widest ml-1 block">Dog Owner Sign-Up URL</label>
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-400">
              <SafeIcon name="ExternalLink" className="w-4 h-4" />
            </div>
            <input 
              value={profile?.signup_url || ''} 
              onChange={e=> setProfile({...profile,signup_url: e.target.value})}
              className="w-full pl-12 pr-5 py-3 bg-indigo-50/50 rounded-2xl font-bold text-sm outline-none border-2 border-indigo-100 focus:border-indigo-500 transition-all text-indigo-900" 
              placeholder="https://yourwebsite.com/signup"
            />
          </div>
          <p className="text-[10px] text-gray-400 font-medium ml-1">
            This URL controls where the Owner Portal “Sign Up Now” button sends new dog owners.
          </p>
        </div>
      </div>

      {/* Pricing Tiers (Informational/Internal Only now if using External URL) */}
      <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <h3 className="text-sm font-black text-gray-400 uppercase tracking-widest mb-6">Internal Package Reference</h3>
        <div className="grid grid-cols-1 gap-6">
          {tiers.map((tier,idx)=> (
            <div key={tier.id} className={`bg-white p-8 rounded-[2.5rem] border-2 shadow-sm transition-all relative ${tier.popular ? 'border-indigo-500 ring-4 ring-indigo-50' : 'border-gray-100'}`}>
              <button 
                onClick={()=> handleRemoveTier(idx)}
                className="absolute top-6 right-6 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                title="Remove Tier"
              >
                <SafeIcon name="Trash2" className="w-5 h-5" />
              </button>
              <div className="flex justify-between items-center mb-6 pr-12">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center font-black">
                    {idx + 1}
                  </div>
                  <h3 className="text-xl font-black text-gray-900">{tier.name || 'Untitled Tier'}</h3>
                </div>
                <label className="flex items-center gap-3 cursor-pointer bg-gray-50 px-5 py-2.5 rounded-xl border border-gray-100">
                  <span className="text-[10px] font-black uppercase text-indigo-600">Featured</span>
                  <input 
                    type="checkbox" 
                    checked={tier.popular} 
                    onChange={e=> handleTierChange(idx,'popular',e.target.checked)}
                    className="w-5 h-5 rounded-lg text-indigo-600"
                  />
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Tier Name</label>
                    <input 
                      placeholder="e.g. Basic Training"
                      value={tier.name} 
                      onChange={e=> handleTierChange(idx,'name',e.target.value)}
                      className="w-full px-5 py-3 bg-gray-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-indigo-500 transition-all text-gray-900" 
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Price ($)</label>
                    <input 
                      type="number"
                      placeholder="0"
                      value={tier.price} 
                      onChange={e=> handleTierChange(idx,'price',e.target.value)}
                      className="w-full px-5 py-3 bg-gray-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-indigo-500 transition-all text-gray-900" 
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Stripe Payment Link</label>
                    <input 
                      type="url"
                      placeholder="https://buy.stripe.com/..."
                      value={tier.payment_link || ''} 
                      onChange={e=> handleTierChange(idx,'payment_link',e.target.value)}
                      className="w-full px-5 py-3 bg-indigo-50/50 rounded-2xl font-bold text-sm text-indigo-900 outline-none border-2 border-indigo-100 focus:border-indigo-500 transition-all" 
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Description</label>
                    <textarea 
                      rows={2}
                      placeholder="Plan focus..."
                      value={tier.description || ''} 
                      onChange={e=> handleTierChange(idx,'description',e.target.value)}
                      className="w-full px-5 py-3 bg-gray-50 rounded-2xl font-medium text-sm outline-none border-2 border-transparent focus:border-indigo-500 resize-none transition-all" 
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Features (One per line)</label>
                    <textarea 
                      rows={4}
                      placeholder="• Feature 1&#10;• Feature 2"
                      value={tier.features.join('\n')} 
                      onChange={e=> handleTierChange(idx,'features',e.target.value)}
                      className="w-full px-5 py-4 bg-gray-50 rounded-2xl font-medium text-sm outline-none border-2 border-transparent focus:border-indigo-500 resize-none transition-all" 
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button 
            onClick={handleAddTier}
            className="w-full py-6 rounded-[2.5rem] border-2 border-dashed border-indigo-200 text-indigo-600 font-black hover:bg-indigo-50 hover:border-indigo-400 transition-all flex items-center justify-center gap-2"
          >
            <SafeIcon name="Plus" className="w-6 h-6" />
            Add Another Pricing Tier
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsSection;